import { defineStore } from 'pinia'
import { ref, shallowRef } from 'vue'
import type { Company, ChunkData } from './types'

export const useCompaniesDataStore = defineStore(
  'insights-companies-data',
  () => {
    // shallowRef for performance; always replace companies.value entirely — in-place nested mutations are not reactive.
    const companies = shallowRef<Record<number, Company>>({})
    const allCompanyIds = ref<Set<number> | null>(null)
    const collectedCompanyIds = ref<Set<number>>(new Set())

    function reset(): void {
      companies.value = {}
      allCompanyIds.value = null
      collectedCompanyIds.value = new Set()
    }

    function setAllCompanyIds(ids: number[]): void {
      allCompanyIds.value = new Set(ids)
    }

    function appendChunk(
      data: ChunkData,
      didAutoSort: () => boolean,
      onFilterError?: (filterKey: string) => void
    ): Set<number> {
      const matchedCompanyIds = new Set<number>()

      const updatedCompanies = { ...companies.value }

      data.matches.forEach((match) => {
        const key = match.company.id
        const existingCompany = updatedCompanies[key] || {}

        updatedCompanies[key] = {
          ...existingCompany,
          ...match,
          id: key,
          filter_values: {
            ...existingCompany.filter_values,
            ...match.filter_values,
          },
        }

        matchedCompanyIds.add(key)

        if (!didAutoSort()) {
          const filterWithError = Object.keys(match.filter_values).find(
            (k) => !!match.filter_values[k].filter_error
          )
          if (filterWithError) {
            onFilterError?.(filterWithError)
          }
        }
      })

      companies.value = updatedCompanies

      // Build a new Set by copying existing entries and adding new ones;
      // assigning a new reference triggers Vue reactivity without O(n²) spreads.
      const nextCollected = new Set(collectedCompanyIds.value)
      for (const id of data.non_matches) nextCollected.add(id)
      for (const id of matchedCompanyIds) nextCollected.add(id)
      collectedCompanyIds.value = nextCollected

      return matchedCompanyIds
    }

    return {
      companies,
      allCompanyIds,
      collectedCompanyIds,
      reset,
      setAllCompanyIds,
      appendChunk,
    }
  }
)
